import React, { useMemo } from "react";

import useProjectContext from "@/services/context/project/use-project-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import useProjectIndicatorsContext from "@/pages/takeoff/context/project-indicators/use-project-indicators-context.ts";
import useDashboardProcess from '@/hooks/use-dashboard-process.ts';

import { StoreyIndicatorsContext, StoreyIndicatorsContextProps } from "./storey-indicators-context-props.ts";

interface StoreyIndicatorsProviderProps extends React.PropsWithChildren {
    storeyId: string;
}

const StoreyIndicatorsProvider: React.FC<StoreyIndicatorsProviderProps> = ({ storeyId, children }) => {
    const { project } = useProjectContext();
    const { projectMeasures } = useProjectMeasureContext();
    const { dashboardProcessStatusQuery } = useDashboardProcess(project.id);

    const { isLoading, projectIndicatorsData, filesPathData } = useProjectIndicatorsContext();

    const processData = dashboardProcessStatusQuery.data;
    const isProcessLoading = dashboardProcessStatusQuery.isLoading;

    const hasSurfaces = processData?.hasSurfaces === true || processData?.hasCalcul1 === true;
    const shouldFetchIndicators = !isProcessLoading && hasSurfaces;

    const { building, storey } = useMemo(() =>
        projectMeasures.getBuildingAndStoreyFromStoreyId(storeyId) ?? { building: undefined, storey: undefined }
        , [projectMeasures, storeyId]);

    const storeyIndicatorsData = useMemo(() => {
        if (projectIndicatorsData == null) return undefined;

        if (!shouldFetchIndicators) {
            return undefined;
        }

        const ib = projectIndicatorsData.buildings.find(b => b.buildingName === building?.name);
        const is = ib?.storeys.find(s => s.storeyName === storey?.name);

        return is?.indicators;
    }, [building?.name, storey?.name, projectIndicatorsData, shouldFetchIndicators]);

    const normalizePathName = (value: string): string =>
        value
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "") // Supprime les accents
            .replace(/[\s_-]/g, "")          // Supprime espaces, _ et -
            .toLowerCase();

    const storeyPathsData = useMemo(() => {
        if (building == null || storey == null) return undefined;

        const pathKeyName = `${building?.name}_${storey?.name}`;
        const normalizedPathKeyName = normalizePathName(pathKeyName);

        return filesPathData?.filter(fp =>
            normalizePathName(fp.name).includes(normalizedPathKeyName)
        );
    }, [building, storey, filesPathData]);

    const contextProps: StoreyIndicatorsContextProps = {
        isLoading,
        storeyIndicatorsData,
        storeyPathsData
    };

    return (
        <StoreyIndicatorsContext.Provider value={contextProps}>
            {children}
        </StoreyIndicatorsContext.Provider>
    );
};

export default StoreyIndicatorsProvider;